// ═══════════════════════════════════════════════════
// Articulos.jsx — Página pública listado de artículos
// ═══════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuthUser } from '../../context/AuthUserContext';
import './Articulos.css';
import { useCart } from '../../context/CartContext';

const API = 'https://centrocomunicar.com/api-comunicar/endpoints/articulos.php';

const LogoComunicar = ({ height = 40 }) => (
  <img 
    src="/images/logo_centro.png" 
    alt="Comunicar" 
    style={{ height: height, width: 'auto', display: 'block' }}
  />
);

const IconArrow    = () => (<svg viewBox="0 0 20 20" fill="none" width="15" height="15"><path d="M4 10h12M11 6l5 4-5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);
const IconSearch   = () => (<svg viewBox="0 0 24 24" fill="none" width="16" height="16"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
const IconCalendar = () => (<svg viewBox="0 0 24 24" fill="none" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
const IconEye      = () => (<svg viewBox="0 0 24 24" fill="none" width="13" height="13"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>);
const IconCart      = () => (<svg viewBox="0 0 24 24" fill="none" width="15" height="15"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);

const CAT_COLORS = {
  Lenguaje: '#4db88a', TEA: '#60a5fa', Habla: '#fbbf24',
  Aprendizaje: '#a78bfa', Familia: '#f9a8d4', Tartamudez: '#fb7185',
  Recursos: '#34d399', General: '#9ca3af',
};

function ArticuloCard({ art, destacado = false }) {
  const color = CAT_COLORS[art.categoria] || '#82D8B3';
  const fecha = new Date(art.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Link to={`/articulos/${art.slug}`} className={`art-card ${destacado ? 'art-card--destacado' : ''}`}>
      <div className="art-card__thumb">
        {art.imagen_portada
          ? <img src={art.imagen_portada} alt={art.titulo}/>
          : <div className="art-card__thumb-placeholder" style={{ background: `linear-gradient(135deg, ${color}22, ${color}55)` }}>
              <span style={{ fontSize: '2rem' }}>📝</span>
            </div>
        }
        {destacado && <span className="art-card__badge-dest">⭐ Destacado</span>}
        {art.categoria && (
          <span className="art-card__badge-cat" style={{ background: color }}>
            {art.categoria}
          </span>
        )}
      </div>
      <div className="art-card__body">
        <h3 className="art-card__titulo">{art.titulo}</h3>
        {art.resumen && <p className="art-card__resumen">{art.resumen.slice(0, 120)}{art.resumen.length > 120 ? '…' : ''}</p>}
        <div className="art-card__meta">
          <span><IconCalendar/> {fecha}</span>
          <span><IconEye/> {art.vistas || 0} vistas</span>
          {art.autor && <span>✍️ {art.autor}</span>}
        </div>
        {art.tags?.length > 0 && (
          <div className="art-card__tags">
            {art.tags.slice(0, 3).map(tag => (
              <span key={tag} className="art-card__tag">#{tag}</span>
            ))}
          </div>
        )}
        <span className="art-card__leer">Leer artículo <IconArrow/></span>
      </div>
    </Link>
  );
}

export default function Articulos() {
  const { estaLogueado, usuario } = useAuthUser();
  const [articulos,   setArticulos]   = useState([]);
  const [categorias,  setCategorias]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [busqueda,    setBusqueda]    = useState('');
  const [catActiva,   setCatActiva]   = useState('');
  const [tagActivo,   setTagActivo]   = useState('');
  const [offset,      setOffset]      = useState(0);
  const LIMIT = 9;
  const { totalItems } = useCart();

  const fetchArticulos = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const off = reset ? 0 : offset;
      const params = new URLSearchParams({ action: 'list', limit: LIMIT, offset: off });
      if (busqueda)  params.set('busqueda', busqueda);
      if (catActiva) params.set('categoria', catActiva);
      if (tagActivo) params.set('tag', tagActivo);
      const r = await fetch(`${API}?${params}`);
      const j = await r.json();
      if (j.success) {
        setArticulos(prev => reset ? (j.data?.items || []) : [...prev, ...(j.data?.items || [])]);
        setTotal(j.data?.total || 0);
        if (reset) setOffset(LIMIT);
        else setOffset(off + LIMIT);
      }
    } catch {}
    finally { setLoading(false); }
  }, [busqueda, catActiva, tagActivo, offset]);

  useEffect(() => {
    fetch(`${API}?action=categorias`).then(r => r.json()).then(j => {
      if (j.success) setCategorias(j.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchArticulos(true); }, [busqueda, catActiva, tagActivo]);

  const destacados = articulos.filter(a => a.destacado);
  const normales   = articulos.filter(a => !a.destacado);

  return (
    <div className="art-page">

<nav className="nav">
        <Link to="/" className="nav__logo"><LogoComunicar height={44}/></Link>
        <div className="nav__links">
          <Link to="/">Inicio</Link>
          <Link to="/formaciones">Cursos</Link>
          <Link to="/recursos">Materiales</Link>
          <Link to="/comunidad">Comunidad</Link>
          <Link to="/articulos" style={{color:'var(--verde-dk)',fontWeight:700}}>Blog</Link>

          <a href="https://centrocomunicar.com/virtual/login/index.php"
            target="_blank" rel="noopener noreferrer"
            className="nav__aula-btn">
            <span className="aula-icon">📖</span>
            Aula Virtual
          </a>


        </div>
        <div className="nav__actions">
          <Link to="/carrito"
            style={{position:'relative',display:'flex',alignItems:'center',padding:'.4rem .7rem',borderRadius:100,border:'1.5px solid rgba(130,216,179,.3)',color:'var(--gris,#5a6a63)',transition:'all .2s'}}
            title="Ver carrito">
            <IconCart/>
            {totalItems > 0 && (
              <span style={{position:'absolute',top:-6,right:-6,background:'#82D8B3',color:'#111614',borderRadius:'50%',width:18,height:18,fontSize:'.62rem',fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>
                {totalItems > 9 ? '9+' : totalItems}
              </span>
            )}
          </Link>
          {estaLogueado
            ? <Link to="/mi-cuenta" className="nav__mi-cuenta">● {usuario.nombre}</Link>
            : <Link to="/login" className="nav__login">Iniciar sesión</Link>
          }
        </div>
      </nav>

      {/* Hero */}
      <div className="art-hero">
        <p className="section-label">Blog y recursos</p>
        <h1>Blog <em>Comunicar</em></h1>
        <p className="art-hero__sub">Acercando la evidencia científica a la práctica profesional.</p>

        {/* Buscador */}
        <div className="art-search">
          <IconSearch/>
          <input
            type="text" placeholder="Buscar artículos…"
            value={busqueda}
            onChange={e => { setBusqueda(e.target.value); setOffset(0); }}
          />
        </div>
      </div>

      {/* Categorías */}
      {categorias.length > 0 && (
        <div className="art-cats">
          <button className={`art-cat-btn ${!catActiva ? 'art-cat-btn--active' : ''}`} onClick={() => setCatActiva('')}>
            Todos
          </button>
          {categorias.map(c => (
            <button key={c.categoria}
              className={`art-cat-btn ${catActiva === c.categoria ? 'art-cat-btn--active' : ''}`}
              style={catActiva === c.categoria ? { background: CAT_COLORS[c.categoria] || '#4db88a', color: '#fff', borderColor: 'transparent' } : {}}
              onClick={() => { setCatActiva(c.categoria); setOffset(0); }}
            >
              {c.categoria} <span className="art-cat-count">{c.total}</span>
            </button>
          ))}
        </div>
      )}

      {tagActivo && (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ background: '#edfaf4', color: '#4db88a', padding: '.3rem .8rem', borderRadius: 100, fontSize: '.82rem', fontWeight: 700 }}>
            #{tagActivo}
            <button style={{ background: 'none', border: 'none', color: '#4db88a', cursor: 'pointer', marginLeft: '.4rem', fontWeight: 700 }}
              onClick={() => setTagActivo('')}>✕</button>
          </span>
        </div>
      )}

      <div className="art-body">

        {/* Destacados */}
        {!busqueda && !catActiva && !tagActivo && destacados.length > 0 && (
          <section className="art-section">
            <div className="art-section__head">
              <h2>⭐ Destacados</h2>
            </div>
            <div className="art-grid art-grid--destacados">
              {destacados.slice(0, 2).map(a => <ArticuloCard key={a.id} art={a} destacado/>)}
            </div>
          </section>
        )}

        {/* Todos */}
        <section className="art-section">
          {(!busqueda && !catActiva && !tagActivo && destacados.length > 0) && (
            <div className="art-section__head">
              <h2>Todos los artículos</h2>
              <span className="art-count">{total} artículos</span>
            </div>
          )}
          {loading && articulos.length === 0 ? (
            <div className="art-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="art-card art-card--skeleton">
                  <div className="art-card__thumb" style={{ background: '#e8f5ef' }}/>
                  <div className="art-card__body">
                    <div className="skel skel--line skel--long"/>
                    <div className="skel skel--line skel--mid"/>
                  </div>
                </div>
              ))}
            </div>
          ) : normales.length === 0 && articulos.length === 0 ? (
            <div className="art-empty">
              <p>{busqueda || catActiva || tagActivo ? 'Sin resultados para tu búsqueda.' : 'No hay artículos publicados aún.'}</p>
            </div>
          ) : (
            <div className="art-grid">
              {(busqueda || catActiva || tagActivo ? articulos : normales).map(a => (
                <ArticuloCard key={a.id} art={a}/>
              ))}
            </div>
          )}

          {/* Cargar más */}
          {!loading && articulos.length < total && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button className="art-load-more" onClick={() => fetchArticulos(false)}>
                Cargar más artículos
              </button>
            </div>
          )}
          {loading && articulos.length > 0 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#5a6a63' }}>Cargando…</div>
          )}
        </section>
      </div>

      <footer className="footer">
        <div className="footer__top">
          <div className="footer__brand">
            <p className="footer__brand-desc">Cursos especializados y materiales terapéuticos para profesionales y familias que trabajan el desarrollo del lenguaje.</p>
            <div className="footer__social">
              <a href="https://www.instagram.com/comunicar_terapias" className="footer__social-btn" aria-label="Instagram"><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg></a>
              <a href="https://www.facebook.com/comunicar.terapias" className="footer__social-btn" aria-label="Facebook"><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></a>
              <a href="https://api.whatsapp.com/send/?phone=51940686868" className="footer__social-btn" aria-label="WhatsApp"><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></a>
            </div>
          </div>
          <div className="footer__cols">
            <div className="footer__col">
              <h4>Cursos</h4>
              <Link to="/formaciones#gratuitos">Cursos gratuitos</Link>
              <Link to="/formaciones">Cursos grabados</Link>
              <Link to="/formaciones">Cursos en vivo</Link>
              <a href="https://centrocomunicar.com/virtual/login/index.php" target="_blank" rel="noopener noreferrer">Aula virtual ↗</a>
            </div>
            <div className="footer__col">
              <h4>Materiales</h4>
              <Link to="/recursos">Imprimibles</Link>
              <Link to="/recursos">Cuadernillos</Link>
              <Link to="/recursos">Juegos terapéuticos</Link>
            </div>
            <div className="footer__col">
              <h4>Comunidad</h4>
              <Link to="/comunidad">Redes sociales</Link>
              <Link to="/comunidad">Boletín mensual</Link>
              <Link to="/comunidad">Programa de puntos</Link>
            </div>
          </div>
          <div className="footer__newsletter">
            <h4>Recibe novedades</h4>
            <p>Nuevos cursos y materiales gratuitos en tu correo.</p>
            <div className="footer__form">
              <input type="email" placeholder="tu@correo.com" className="footer__input"/>
              <button className="footer__submit"><IconArrow/></button>
            </div>
            <div className="footer__legal-links">
              <Link to="/atencion-cliente">Atención al cliente</Link>
              <Link to="/legal">Legal</Link>
              <Link to="/libro-reclamaciones">Libro de reclamaciones</Link>
            </div>
          </div>
        </div>
        <div className="footer__bottom">
          <p>© 2026 Comunicar – Desarrollo y Lenguaje. Todos los derechos reservados.</p>
          <div className="footer__legal">
            <Link to="/legal">Privacidad</Link>
            <Link to="/legal">Términos y condiciones</Link>
            <Link to="/politica-devoluciones">Política de devoluciones</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}


// ═══════════════════════════════════════════════════
// ArticuloDetalle.jsx — Página de detalle de artículo
// ═══════════════════════════════════════════════════
// Este componente va en un archivo separado: ArticuloDetalle.jsx

/*
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../context/AuthUserContext';
import './ArticuloDetalle.css';

const API = 'https://centrocomunicar.com/api-comunicar/endpoints/articulos.php';

const CAT_COLORS = {
  Lenguaje: '#4db88a', TEA: '#60a5fa', Habla: '#fbbf24',
  Aprendizaje: '#a78bfa', Familia: '#f9a8d4', Tartamudez: '#fb7185',
  Recursos: '#34d399', General: '#9ca3af',
};

export default function ArticuloDetalle() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { estaLogueado, usuario } = useAuthUser();
  const [art,     setArt]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchArt = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}?action=detail&slug=${slug}`);
        const j = await r.json();
        if (!j.success) { navigate('/articulos'); return; }
        setArt(j.data);
        // Sumar vista
        fetch(`${API}?action=sumar_vista`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: j.data.id }),
        }).catch(() => {});
      } catch { navigate('/articulos'); }
      finally { setLoading(false); }
    };
    fetchArt();
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v=)([^&]+)/);
    return m ? m[1] : url;
  };

  if (loading) return (
    <div className="artd-page">
      <div style={{ textAlign: 'center', padding: '5rem', color: '#5a6a63' }}>Cargando artículo…</div>
    </div>
  );

  if (!art) return null;

  const fecha = new Date(art.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });
  const color = CAT_COLORS[art.categoria] || '#82D8B3';
  const youtubeId = getYoutubeId(art.video_youtube);

  return (
    <div className="artd-page">

      <nav className="nav">
        <Link to="/" className="nav__logo"><LogoComunicar height={44}/></Link>
        <div className="nav__links">
          <Link to="/">Inicio</Link>
          <Link to="/formaciones">Cursos</Link>
          <Link to="/recursos">Materiales</Link>
          <Link to="/articulos" style={{ color: 'var(--verde-dk,#4db88a)', fontWeight: 700 }}>Artículos</Link>
          {estaLogueado
            ? <Link to="/mi-cuenta" className="nav__mi-cuenta">● {usuario.nombre}</Link>
            : <Link to="/login" className="nav__login">Iniciar sesión</Link>
          }
        </div>
      </nav>

      <div className="artd-breadcrumb">
        <Link to="/articulos">← Artículos</Link>
        {art.categoria && <><span>/</span><span>{art.categoria}</span></>}
      </div>

      <article className="artd-body">

        <header className="artd-header">
          {art.categoria && (
            <span className="artd-cat" style={{ background: color }}>{art.categoria}</span>
          )}
          <h1 className="artd-titulo">{art.titulo}</h1>
          <div className="artd-meta">
            <span>✍️ {art.autor}</span>
            <span>📅 {fecha}</span>
            <span>👁 {art.vistas} vistas</span>
          </div>
          {art.resumen && <p className="artd-resumen">{art.resumen}</p>}
          <div className="artd-share">
            <button onClick={copyLink} className="artd-share-btn">
              {copied ? '✓ Copiado' : '🔗 Copiar enlace'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(art.titulo + ' ' + window.location.href)}`}
              target="_blank" rel="noopener noreferrer" className="artd-share-btn artd-share-btn--wa"
            >
              📲 Compartir por WhatsApp
            </a>
          </div>
        </header>

        {art.imagen_portada && (
          <img src={art.imagen_portada} alt={art.titulo} className="artd-portada"/>
        )}

        {youtubeId && (
          <div className="artd-video">
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={art.titulo} allowFullScreen
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        )}

        {art.contenido && (
          <div className="artd-contenido" dangerouslySetInnerHTML={{ __html: art.contenido }}/>
        )}

        {art.imagenes?.length > 0 && (
          <div className="artd-galeria">
            <h3>Galería</h3>
            <div className="artd-galeria__grid">
              {art.imagenes.map(img => (
                <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer">
                  <img src={img.url} alt=""/>
                </a>
              ))}
            </div>
          </div>
        )}

        {art.tags?.length > 0 && (
          <div className="artd-tags">
            {art.tags.map(tag => (
              <Link key={tag} to={`/articulos?tag=${tag}`} className="artd-tag">#{tag}</Link>
            ))}
          </div>
        )}

      </article>

      {art.relacionados?.length > 0 && (
        <section className="artd-relacionados">
          <h2>Artículos relacionados</h2>
          <div className="artd-rel-grid">
            {art.relacionados.map(rel => (
              <Link key={rel.id} to={`/articulos/${rel.slug}`} className="artd-rel-card">
                {rel.imagen_portada && <img src={rel.imagen_portada} alt={rel.titulo}/>}
                <div>
                  <strong>{rel.titulo}</strong>
                  {rel.resumen && <p>{rel.resumen.slice(0, 80)}…</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="footer">
        <div className="footer__bottom" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>© 2026 Comunicar – Desarrollo y Lenguaje. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
*/